import type { FirstPersonController } from "../player/FirstPersonController";

/**
 * AWP scope overlay — CS:GO-style sniper scope.
 *
 * Pure DOM, shown while `FirstPersonController.isScoped()`:
 *   - circular lens with the rest of the screen blacked out
 *   - full-length hairline reticle (thin black cross)
 *   - soft vignette shading toward the lens rim
 *
 * The regular dynamic crosshair hides itself whenever a sniper is
 * equipped (Crosshair.ts), so this reticle is the only aim reference
 * while scoped — and noscope is intentionally blind, like CS:GO.
 */
export class ScopeOverlay {
  private readonly controller: FirstPersonController;
  private readonly root: HTMLDivElement;
  private rafHandle = 0;
  private visible = false;
  private lastZoomLevel = 0;

  constructor(controller: FirstPersonController) {
    this.controller = controller;

    this.root = document.createElement("div");
    this.root.id = "scope-overlay";

    const lens = document.createElement("div");
    lens.className = "scope-lens";
    const hairV = document.createElement("div");
    hairV.className = "scope-hair scope-hair-v";
    const hairH = document.createElement("div");
    hairH.className = "scope-hair scope-hair-h";

    this.root.appendChild(lens);
    this.root.appendChild(hairV);
    this.root.appendChild(hairH);
    document.body.appendChild(this.root);

    this.injectStyles();
    this.rafHandle = requestAnimationFrame(this.tick);
  }

  destroy(): void {
    cancelAnimationFrame(this.rafHandle);
    this.root.remove();
  }

  private readonly tick = (): void => {
    const zoom = this.controller.getZoomLevel();
    const scoped = zoom > 0;
    if (scoped !== this.visible) {
      this.visible = scoped;
      this.root.classList.toggle("scope-on", scoped);
    }
    // Re-trigger the small "settle" pulse when stepping zoom1 → zoom2 so
    // the level change reads even though the overlay itself is static.
    if (scoped && zoom !== this.lastZoomLevel) {
      this.root.classList.remove("scope-pulse");
      // Force reflow so the animation restarts.
      void this.root.offsetWidth;
      this.root.classList.add("scope-pulse");
    }
    this.lastZoomLevel = zoom;
    this.rafHandle = requestAnimationFrame(this.tick);
  };

  private injectStyles(): void {
    if (document.getElementById("scope-overlay-styles")) return;
    const style = document.createElement("style");
    style.id = "scope-overlay-styles";
    style.textContent = `
      #scope-overlay {
        position: fixed;
        inset: 0;
        display: none;
        pointer-events: none;
        z-index: 40;
        overflow: hidden;
      }
      #scope-overlay.scope-on { display: block; }
      #scope-overlay .scope-lens {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 96vmin;
        height: 96vmin;
        transform: translate(-50%, -50%);
        border-radius: 50%;
        /* Massive outer shadow blacks out everything beyond the lens;
           inset shadows shade the glass toward the rim. */
        box-shadow:
          0 0 0 200vmax #000,
          inset 0 0 14vmin rgba(0, 0, 0, 0.9),
          inset 0 0 4vmin rgba(0, 0, 0, 0.55);
      }
      #scope-overlay .scope-hair {
        position: absolute;
        background: #000;
      }
      #scope-overlay .scope-hair-v {
        left: calc(50% - 1px);
        top: 0;
        width: 2px;
        height: 100%;
      }
      #scope-overlay .scope-hair-h {
        top: calc(50% - 1px);
        left: 0;
        height: 2px;
        width: 100%;
      }
      #scope-overlay.scope-pulse .scope-lens {
        animation: scope-settle 110ms ease-out;
      }
      @keyframes scope-settle {
        from { transform: translate(-50%, -50%) scale(1.04); }
        to   { transform: translate(-50%, -50%) scale(1); }
      }
    `;
    document.head.appendChild(style);
  }
}
